import { supabase } from 'services/supabaseClient';
import {
  buildCouponDates,
  isFixedCouponLike,
} from 'pages/FundPlanner/components/Portfolios/portfolioCashFlows';
import { buildSlgsNoteCashflows } from 'pages/FundPlanner/lib/slgsNoteSchedule';

function addCalendarDays(date, days) {
  const result = new Date(date.getTime());
  result.setDate(result.getDate() + days);
  return `${result.getFullYear()}-${String(result.getMonth() + 1).padStart(2, '0')}-${String(result.getDate()).padStart(2, '0')}`;
}

export function getMaturityReminderStatus(
  maturityDate,
  today = new Date(),
  lookaheadDays = 14
) {
  if (maturityDate < addCalendarDays(today, 0)) {
    return { label: 'Past', tone: 'red' };
  }
  const nearDays = Math.min(7, lookaheadDays);
  if (maturityDate <= addCalendarDays(today, nearDays)) {
    return { label: `Next ${nearDays} days`, tone: 'amber' };
  }
  return { label: `Next ${lookaheadDays} days`, tone: 'green' };
}

export function buildMaturityMessages({
  funds,
  positions,
  securities,
  today = new Date(),
  lookbackDays = 7,
  lookaheadDays = 14,
}) {
  const todayIso = addCalendarDays(today, 0);
  const firstDate = addCalendarDays(today, -lookbackDays);
  const lastDate = addCalendarDays(today, lookaheadDays);
  const fundById = new Map(funds.map((fund) => [fund.id, fund]));
  const securityByCusip = new Map(securities.map((security) => [security.cusip, security]));

  const seenMaturities = new Set();
  const seenInterestPayments = new Set();
  const messages = [];

  positions.forEach((position) => {
    const fund = fundById.get(position.fund_id);
    if (!fund) return;

    const security = securityByCusip.get(position.cusip_or_id);
    const maturityDate = position.raw_maturity_date || security?.maturity_date;
    const investmentType = position.investment_type === 'marketable_security'
      ? 'Marketable Security'
      : position.investment_type || 'Unknown';

    if (
      maturityDate &&
      maturityDate >= firstDate &&
      maturityDate <= lastDate &&
      position.purchase_date <= maturityDate &&
      (!position.closed_date || position.closed_date >= maturityDate)
    ) {
      // Several lots of one security in a fund share a single maturity event.
      const eventKey = `${fund.id}:${position.cusip_or_id}:${maturityDate}`;
      if (!seenMaturities.has(eventKey)) {
        seenMaturities.add(eventKey);
        messages.push({
          id: `maturity:${eventKey}`,
          eventType: 'maturity',
          fundName: fund.name,
          investmentType,
          identifier: position.cusip_or_id,
          eventDate: maturityDate,
          maturityDate,
          matured: maturityDate < todayIso,
        });
      }
    }

    if (!maturityDate) return;

    let interestDates = [];
    const isSlgsTimeDeposit = String(position.investment_type || '').trim().toLowerCase() === 'slgs time deposit';
    const settlementDate = position.settlement_date || position.purchase_date;

    if (security && isFixedCouponLike({
      coupon: position.raw_coupon ?? security.coupon,
      coupon_type: security.coupon_type,
      security_type: security.security_type,
      investment_type: position.investment_type,
    })) {
      // buildCouponDates uses an exclusive start; subtract one more day so the
      // Dashboard's seven-days-past boundary remains inclusive.
      interestDates = buildCouponDates({
        coupon: position.raw_coupon ?? security.coupon,
        coupon_type: security.coupon_type,
        security_type: security.security_type,
        investment_type: position.investment_type,
        payment_frequency: security.payment_frequency,
        first_interest_payment_date: security.first_interest_payment_date,
        maturity_date: maturityDate,
      }, addCalendarDays(today, -(lookbackDays + 1)), lastDate);
    } else if (
      isSlgsTimeDeposit &&
      settlementDate &&
      maturityDate > addYearsIso(settlementDate, 1) &&
      Number(position.raw_coupon) > 0
    ) {
      interestDates = buildSlgsNoteCashflows({
        settlement: settlementDate,
        maturity: maturityDate,
        principal: 1,
        ratePct: Number(position.raw_coupon),
        descriptionBase: position.cusip_or_id || 'SLGS',
      }).regular.map((flow) => flow.date)
        .filter((date) => date >= firstDate && date <= lastDate);
    }

    interestDates.forEach((paymentDate) => {
      if (position.purchase_date > paymentDate) return;
      if (position.closed_date && position.closed_date < paymentDate) return;

      const eventKey = `${fund.id}:${position.cusip_or_id}:${paymentDate}`;
      if (seenInterestPayments.has(eventKey)) return;
      seenInterestPayments.add(eventKey);
      messages.push({
        id: `interest:${eventKey}`,
        eventType: 'interest',
        fundName: fund.name,
        investmentType,
        identifier: position.cusip_or_id,
        eventDate: paymentDate,
        maturityDate,
        matured: paymentDate < todayIso,
      });
    });
  });

  return messages.sort((a, b) =>
    a.eventDate.localeCompare(b.eventDate) ||
    a.fundName.localeCompare(b.fundName) ||
    a.eventType.localeCompare(b.eventType)
  );
}

function addYearsIso(dateIso, years) {
  const [year, month, day] = String(dateIso).split('-').map(Number);
  if (!year || !month || !day) return '';
  const result = new Date(Date.UTC(year + years, month - 1, day));
  if (result.getUTCMonth() !== month - 1) result.setUTCDate(0);
  return result.toISOString().slice(0, 10);
}

export async function loadMaturityMessages(orgId, options = {}) {
  const funds = [];
  for (let offset = 0; ; offset += 1000) {
    const { data, error } = await supabase
      .from('fp_investment_funds')
      .select('id, name')
      .eq('org_id', orgId)
      .order('id')
      .range(offset, offset + 999);
    if (error) throw error;
    funds.push(...(data || []));
    if (!data || data.length < 1000) break;
  }
  if (!funds.length) return [];

  const positions = [];
  for (let index = 0; index < funds.length; index += 100) {
    const fundIds = funds.slice(index, index + 100).map((fund) => fund.id);
    for (let offset = 0; ; offset += 1000) {
      const { data, error } = await supabase
        .from('fp_positions')
        .select('id, fund_id, cusip_or_id, purchase_date, settlement_date, closed_date, raw_coupon, raw_maturity_date, investment_type')
        .in('fund_id', fundIds)
        .order('id')
        .range(offset, offset + 999);
      if (error) throw error;
      positions.push(...(data || []));
      if (!data || data.length < 1000) break;
    }
  }

  const identifiers = [...new Set(positions
    .filter((p) => String(p.investment_type || '').trim().toLowerCase() !== 'slgs time deposit')
    .map((p) => p.cusip_or_id)
    .filter(Boolean))];
  const securities = [];
  for (let index = 0; index < identifiers.length; index += 100) {
    const { data, error } = await supabase
      .from('fp_security_master')
      .select('cusip, maturity_date, coupon, coupon_type, security_type, payment_frequency, first_interest_payment_date')
      .in('cusip', identifiers.slice(index, index + 100));
    if (error) throw error;
    securities.push(...(data || []));
  }

  return buildMaturityMessages({ funds, positions, securities, ...options });
}
